import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, controlToastState, Toast } from "risa-oasis-ui_v2";

import AddStepDialog, {
  ProcessStepData,
} from "../../../../../components/modals/AddStepDialog/AddStepDialog";
import DeleteStepConfirmationDialog from "../../../../../components/modals/DeleteStepConfirmationDialog/DeleteStepConfirmationDialog";
import SwapConfirmationDialog from "../../../../../components/modals/SwapConfirmationDialog";
import {
  addProcessStep,
  deleteProcessStep,
  fetchRpaVersion,
  setSelectedVersion,
  swapProcessSteps,
  updateRpaVersion,
} from "../../../../../redux/slice/rpaConfiguration";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { AddIcon } from "../../../../../svg/add-icon";
import { SwapIcon } from "../../../../../svg/swap-icon";
import "./ProcessSteps.scss";

// Helper function to get current timestamp in IST timezone
const getCurrentTimestamp = (): string => {
  return moment()
    .utcOffset("+05:30")
    .format("MMMM DD, YYYY [at] h:mm:ss A [UTC+5:30]");
};

interface ConfigurationElement {
  [key: string]: any;
}

interface ProcessStepsProps {
  steps: ConfigurationElement[];
}

const ProcessSteps: React.FC<ProcessStepsProps> = ({ steps }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedOrganization, selectedVersion } = useSelector(
    (state: RootState) => state.rpaConfiguration,
  );
  const authState = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<ConfigurationElement>({});
  const [isSaving, setIsSaving] = useState(false);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swapIndices, setSwapIndices] = useState<{
    step1: number;
    step2: number;
  } | null>(null);
  const [swappingSteps, setSwappingSteps] = useState<{
    step1: number;
    step2: number;
  } | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteStepIndex, setDeleteStepIndex] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addStepIndex, setAddStepIndex] = useState<number>(0);
  const [isAdding, setIsAdding] = useState(false);

  // Clean up dialog state when steps array changes
  useEffect(() => {
    if (deleteStepIndex !== null && deleteStepIndex >= steps.length) {
      console.log("Cleaning up delete dialog - step index out of bounds");
      setDeleteDialogOpen(false);
      setDeleteStepIndex(null);
    }
  }, [steps.length, deleteStepIndex]);

  const toggleStep = (index: number) => {
    setExpandedStep(expandedStep === index ? null : index);
    if (editingStep === index) {
      setEditingStep(null);
      setEditedData({});
    }
  };

  const handleEditClick = (index: number, config: ConfigurationElement) => {
    setEditingStep(index);
    setEditedData({ ...config });
  };

  const handleCancelEdit = () => {
    setEditingStep(null);
    setEditedData({});
  };

  const handleSaveEdit = async () => {
    if (editingStep === null || !selectedOrganization || !selectedVersion)
      return;

    setIsSaving(true);
    try {
      // Get current user email from auth state
      const userEmail = authState?.user?.email || "unknown";

      // Create new configuration array with updated step
      const newConfiguration = [...selectedVersion.configuration];
      newConfiguration[editingStep] = editedData;

      // Prepare update data
      const updateData = {
        configuration: newConfiguration,
        updated_at: getCurrentTimestamp(),
        updated_by: userEmail,
      };

      // Optimistic update: immediately update the local state
      dispatch(
        setSelectedVersion({
          ...selectedVersion,
          ...updateData,
        }),
      );

      // Update Firebase
      await dispatch(
        updateRpaVersion({
          organizationId: selectedOrganization.id,
          versionId: selectedVersion.id,
          data: updateData,
        }),
      ).unwrap();

      // Show success toast
      controlToastState("edit-success", true);

      // Exit edit mode
      setEditingStep(null);
      setEditedData({});
    } catch (error) {
      console.error("Failed to save step changes:", error);
      controlToastState("edit-error", true);

      // Revert the optimistic update on error by refetching the version
      if (selectedOrganization && selectedVersion) {
        dispatch(
          fetchRpaVersion({
            organizationId: selectedOrganization.id,
            versionId: selectedVersion.id,
          }),
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (fieldPath: string, value: any) => {
    setEditedData((prev) => {
      const updated = { ...prev };
      const keys = fieldPath.split(".");
      let current = updated;

      // Navigate to the parent of the target field
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      // Set the final value
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const handleDeleteClick = useCallback((stepIndex: number) => {
    setDeleteStepIndex(stepIndex);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (
      deleteStepIndex === null ||
      !selectedOrganization ||
      !selectedVersion ||
      isDeleting
    )
      return;

    // Prevent double calls
    if (isDeleting) {
      console.log("Delete already in progress, ignoring call");
      return;
    }

    setIsDeleting(true);
    const stepToDelete = deleteStepIndex;

    try {
      console.log("=== DELETE OPERATION START ===");
      console.log("Step index to delete:", stepToDelete);
      console.log(
        "Current configuration length:",
        selectedVersion.configuration.length,
      );
      console.log(
        "Current configuration:",
        selectedVersion.configuration.map((step, idx) => ({
          index: idx,
          name: step.name || step.title || step.type || `Step ${idx + 1}`,
          data: step,
        })),
      );
      console.log(
        "Step to delete:",
        selectedVersion.configuration[stepToDelete],
      );

      // Update Firebase - let Redux handle the state update
      const result = await dispatch(
        deleteProcessStep({
          organizationId: selectedOrganization.id,
          versionId: selectedVersion.id,
          stepIndex: stepToDelete,
        }),
      ).unwrap();

      console.log("Delete operation result:", result);
      console.log("New configuration length:", result.configuration?.length);
      console.log("=== DELETE OPERATION END ===");

      // Show success toast
      controlToastState("delete-success", true);

      // Close delete dialog
      setDeleteDialogOpen(false);
      setDeleteStepIndex(null);

      // Reset edit mode if we were editing the deleted step or a step after it
      if (editingStep !== null) {
        if (editingStep === stepToDelete) {
          // We were editing the deleted step
          setEditingStep(null);
          setEditedData({});
        } else if (editingStep > stepToDelete) {
          // We were editing a step after the deleted one, adjust the index
          setEditingStep(editingStep - 1);
        }
      }

      // Reset expanded step if needed
      if (expandedStep !== null) {
        if (expandedStep === stepToDelete) {
          // We had expanded the deleted step
          setExpandedStep(null);
        } else if (expandedStep > stepToDelete) {
          // We had expanded a step after the deleted one, adjust the index
          setExpandedStep(expandedStep - 1);
        }
      }
    } catch (error) {
      console.error("Failed to delete step:", error);
      controlToastState("delete-error", true);
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteStepIndex,
    selectedOrganization,
    selectedVersion,
    isDeleting,
    dispatch,
    editingStep,
    expandedStep,
  ]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteStepIndex(null);
  }, []);

  const handleSwapClick = (step1Index: number, step2Index: number) => {
    setSwapIndices({ step1: step1Index, step2: step2Index });
    setSwapDialogOpen(true);
  };

  const handleSwapConfirm = async () => {
    if (!swapIndices || !selectedOrganization || !selectedVersion) return;

    setIsSwapping(true);
    setSwappingSteps(swapIndices);

    try {
      console.log("Starting swap operation:", {
        organizationId: selectedOrganization.id,
        versionId: selectedVersion.id,
        step1Index: swapIndices.step1,
        step2Index: swapIndices.step2,
        step1Before: selectedVersion.configuration[swapIndices.step1],
        step2Before: selectedVersion.configuration[swapIndices.step2],
      });

      // Update Firebase first (no optimistic update)
      const result = await dispatch(
        swapProcessSteps({
          organizationId: selectedOrganization.id,
          versionId: selectedVersion.id,
          step1Index: swapIndices.step1,
          step2Index: swapIndices.step2,
        }),
      ).unwrap();

      console.log("Swap operation completed successfully. Result:", result);

      // Verify the swap worked correctly
      if (result && result.configuration) {
        const step1After = result.configuration[swapIndices.step1];
        const step2After = result.configuration[swapIndices.step2];
        const step1Before = selectedVersion.configuration[swapIndices.step1];
        const step2Before = selectedVersion.configuration[swapIndices.step2];

        console.log("Swap verification:", {
          step1Before,
          step1After,
          step2Before,
          step2After,
          swappedCorrectly:
            JSON.stringify(step1After) === JSON.stringify(step2Before) &&
            JSON.stringify(step2After) === JSON.stringify(step1Before),
        });
      }

      // Show success toast
      controlToastState("swap-success", true);
    } catch (error) {
      console.error("Failed to swap steps:", error);

      // Show error toast with specific error message
      controlToastState("swap-error", true);

      // Don't update local state if Firebase update failed
    } finally {
      setIsSwapping(false);
      setSwappingSteps(null);
      setSwapDialogOpen(false);
      setSwapIndices(null);
    }
  };

  const handleSwapCancel = () => {
    setSwapDialogOpen(false);
    setSwapIndices(null);
  };

  const handleAddClick = (index: number) => {
    setAddStepIndex(index);
    setAddDialogOpen(true);
  };

  const handleAddConfirm = async (stepData: ProcessStepData) => {
    if (!selectedOrganization || !selectedVersion) return;

    setIsAdding(true);

    try {
      console.log("Starting add step operation:", {
        organizationId: selectedOrganization.id,
        versionId: selectedVersion.id,
        stepData,
        insertIndex: addStepIndex,
      });

      // Update Firebase and state
      const result = await dispatch(
        addProcessStep({
          organizationId: selectedOrganization.id,
          versionId: selectedVersion.id,
          stepData,
          insertIndex: addStepIndex,
        }),
      ).unwrap();

      console.log("Add step operation completed successfully. Result:", result);

      // Show success toast
      controlToastState("add-success", true);

      // Close add dialog
      setAddDialogOpen(false);
      setAddStepIndex(0);
    } catch (error) {
      console.error("Failed to add step:", error);
      controlToastState("add-error", true);
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddCancel = () => {
    setAddDialogOpen(false);
    setAddStepIndex(0);
  };

  if (!steps || steps.length === 0) {
    return (
      <div className="process-steps__empty">
        <p>No process configurations found for this version.</p>
      </div>
    );
  }

  const getConfigurationTitle = (
    config: ConfigurationElement | undefined,
    index: number,
  ): string => {
    // Handle undefined config (e.g., when step is being deleted)
    if (!config || typeof config !== "object") {
      return `Configuration ${index + 1}`;
    }

    // Try to find a meaningful title from common fields
    if (config.name) return config.name;
    if (config.title) return config.title;
    if (config.type) return config.type;
    if (config.stepType) return config.stepType;
    if (config.action) return config.action;
    if (config.id) return config.id;

    // If no meaningful title found, use the first non-array key
    const keys = Object.keys(config);
    const firstStringKey = keys.find((key) => typeof config[key] === "string");
    if (firstStringKey) return `${firstStringKey}: ${config[firstStringKey]}`;

    return `Configuration ${index + 1}`;
  };

  const getConfigurationDescription = (
    config: ConfigurationElement,
  ): string | null => {
    if (config.description) return config.description;
    if (config.summary) return config.summary;
    if (config.details) return config.details;
    return null;
  };

  // Recursive function to render nested object fields
  const renderNestedFields = (
    obj: any,
    parentPath: string = "",
    isEditing: boolean = false,
    depth: number = 0,
  ): JSX.Element[] => {
    const elements: JSX.Element[] = [];

    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
      return elements;
    }

    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;
      const fieldKey = `${currentPath}-${depth}`;

      if (Array.isArray(value)) {
        // Handle arrays separately
        elements.push(
          <div key={fieldKey} className="process-step__array-field">
            {renderArrayValue(value, key, currentPath, isEditing)}
          </div>,
        );
      } else if (typeof value === "object" && value !== null) {
        // Nested object - show header and recurse
        elements.push(
          <div key={fieldKey} className="process-step__nested-object">
            <div className="process-step__nested-header">
              <span className="process-step__nested-title">{key}</span>
            </div>
            <div className="process-step__nested-content">
              {renderNestedFields(value, currentPath, isEditing, depth + 1)}
            </div>
          </div>,
        );
      } else {
        // Simple field
        elements.push(
          <div key={fieldKey} className="process-step__field">
            <span className="process-step__field-key">{key}:</span>
            {isEditing ? (
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleFieldChange(currentPath, e.target.value)}
                className="process-step__field-input"
              />
            ) : (
              <span className="process-step__field-value">{String(value)}</span>
            )}
          </div>,
        );
      }
    });

    return elements;
  };

  const renderArrayValue = (
    arrayValue: any[],
    keyName: string,
    fieldPath: string = "",
    isEditing: boolean = false,
  ) => {
    if (!Array.isArray(arrayValue) || arrayValue.length === 0) {
      return <span className="process-step__empty-array">Empty array</span>;
    }

    return (
      <div className="process-step__array-container">
        <div className="process-step__array-header">
          <span className="process-step__array-title">{keyName}</span>
          <span className="process-step__array-count">
            {arrayValue.length} items
          </span>
        </div>
        <div className="process-step__array-items">
          {arrayValue.map((item, itemIndex) => (
            <div key={itemIndex} className="process-step__array-item">
              <div className="process-step__array-item-header">
                <span className="process-step__array-item-index">
                  #{itemIndex + 1}
                </span>
              </div>
              <div className="process-step__array-item-content">
                {typeof item === "object" && item !== null ? (
                  renderNestedFields(
                    item,
                    `${fieldPath}[${itemIndex}]`,
                    isEditing,
                    0,
                  )
                ) : (
                  <div className="process-step__array-item-field">
                    {isEditing ? (
                      <input
                        type="text"
                        value={String(item)}
                        onChange={(e) => {
                          const newArray = [...arrayValue];
                          newArray[itemIndex] = e.target.value;
                          handleFieldChange(fieldPath, newArray);
                        }}
                        className="process-step__field-input"
                      />
                    ) : (
                      <span className="process-step__array-item-value">
                        {String(item)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderConfigurationContent = (
    config: ConfigurationElement,
    stepIndex: number,
  ) => {
    const isEditing = editingStep === stepIndex;
    const dataToRender = isEditing ? editedData : config;

    const entries = Object.entries(dataToRender);

    // Separate simple values from array/object values
    const simpleEntries = entries.filter(
      ([, value]) =>
        !Array.isArray(value) && (typeof value !== "object" || value === null),
    );
    const complexEntries = entries.filter(
      ([, value]) =>
        Array.isArray(value) || (typeof value === "object" && value !== null),
    );

    return (
      <div className="process-step__configuration-content">
        {/* Edit Controls */}
        <div className="process-step__edit-controls">
          {!isEditing ? (
            <Button
              onClick={() => handleEditClick(stepIndex, config)}
              buttonType="primary"
              size="small"
              disabled={isSwapping}
            >
              Edit Step
            </Button>
          ) : (
            <div className="process-step__edit-actions">
              <Button
                onClick={handleSaveEdit}
                buttonType="primary"
                size="small"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
              <Button
                onClick={handleCancelEdit}
                buttonType="secondary"
                size="small"
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        {/* Render simple fields */}
        {simpleEntries.length > 0 && (
          <div className="process-step__simple-fields">
            {renderNestedFields(
              Object.fromEntries(simpleEntries),
              "",
              isEditing,
              0,
            )}
          </div>
        )}

        {/* Render complex fields (arrays and nested objects) */}
        {complexEntries.length > 0 && (
          <div className="process-step__complex-fields">
            {renderNestedFields(
              Object.fromEntries(complexEntries),
              "",
              isEditing,
              0,
            )}
          </div>
        )}
      </div>
    );
  };

  const getConfigurationType = (
    config: ConfigurationElement,
  ): string | null => {
    if (config.type) return config.type;
    if (config.category) return config.category;
    if (config.stepType) return config.stepType;
    return null;
  };

  const hasExpandableContent = (config: ConfigurationElement): boolean => {
    return (
      Object.keys(config).length > 2 ||
      Object.values(config).some((value) => Array.isArray(value))
    );
  };

  const isStepSwapping = (index: number): boolean => {
    return (
      swappingSteps !== null &&
      (swappingSteps.step1 === index || swappingSteps.step2 === index)
    );
  };

  return (
    <div className="process-steps">
      <div className="process-steps__container">
        {/* Add button before first step */}
        <div className="process-steps__add-section">
          <Button
            onClick={() => handleAddClick(0)}
            buttonType="secondary"
            size="small"
            disabled={isSwapping || isAdding}
          >
            <AddIcon width="16" height="16" color="#0056d6" />
            Add Step Here
          </Button>
        </div>

        {steps.map((config, index) => {
          const isExpanded = expandedStep === index;
          const configTitle = getConfigurationTitle(config, index);
          const configDescription = getConfigurationDescription(config);
          const configurationType = getConfigurationType(config);
          const hasContent = hasExpandableContent(config);
          const isSwappingAnimation = isStepSwapping(index);

          const stepKey = `step-${index}-${config ? getConfigurationTitle(config, index) : "deleted"}`;

          return (
            <React.Fragment key={stepKey}>
              <div
                className={`process-step ${isExpanded ? "process-step--expanded" : ""} ${
                  isSwappingAnimation ? "process-step--swapping" : ""
                }`}
              >
                {/* Step Number Indicator */}
                <div className="process-step__indicator">
                  <div className="process-step__number">{index + 1}</div>
                  {index < steps.length - 1 && (
                    <div className="process-step__connector"></div>
                  )}
                </div>

                {/* Configuration Content */}
                <div className="process-step__card">
                  <div
                    className="process-step__header"
                    onClick={() => hasContent && toggleStep(index)}
                    style={{
                      cursor: hasContent ? "pointer" : "default",
                    }}
                  >
                    <div className="process-step__header-content">
                      <h4 className="process-step__title">{configTitle}</h4>
                      {configurationType && (
                        <span className="process-step__type">
                          {configurationType}
                        </span>
                      )}
                      {/* Show summary of arrays and fields */}
                      <div className="process-step__summary">
                        {Object.entries(config)
                          .map(([key, value]) => {
                            if (Array.isArray(value)) {
                              return (
                                <span
                                  key={key}
                                  className="process-step__summary-item"
                                >
                                  {key}: {value.length} items
                                </span>
                              );
                            }
                            return null;
                          })
                          .filter(Boolean)}
                      </div>
                    </div>

                    <div className="process-step__header-icons">
                      {hasContent && (
                        <div
                          className={`process-step__expand-icon ${isExpanded ? "process-step__expand-icon--expanded" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStep(index);
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                          >
                            <path d="M4 6l4 4 4-4H4z" />
                          </svg>
                        </div>
                      )}

                      {/* Delete Button */}
                      <div className="process-step__delete-button">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(index);
                          }}
                          buttonType="secondary"
                          size="small"
                          disabled={isSwapping || isDeleting}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M18 6L6 18M6 6L18 18"
                              stroke="#e74c3c"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>

                  {configDescription && (
                    <div className="process-step__description">
                      {configDescription}
                    </div>
                  )}

                  {isExpanded && renderConfigurationContent(config, index)}
                </div>
              </div>

              {/* Add and Swap buttons between steps */}
              {index < steps.length - 1 && (
                <div className="process-steps__action-section">
                  <div className="process-steps__action-connector"></div>
                  <div className="process-steps__action-buttons">
                    <Button
                      onClick={() => handleAddClick(index + 1)}
                      buttonType="secondary"
                      size="small"
                      disabled={isSwapping || isAdding}
                    >
                      <AddIcon width="14" height="14" color="#0056d6" />
                    </Button>
                    <Button
                      onClick={() => handleSwapClick(index, index + 1)}
                      buttonType="secondary"
                      size="small"
                      disabled={isSwapping || isAdding}
                    >
                      <SwapIcon width="14" height="14" color="#0056d6" />
                    </Button>
                  </div>
                  <div className="process-steps__action-connector"></div>
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Add button after last step */}
        <div className="process-steps__add-section">
          <Button
            onClick={() => handleAddClick(steps.length)}
            buttonType="secondary"
            size="small"
            disabled={isSwapping || isAdding}
          >
            <AddIcon width="16" height="16" color="#0056d6" />
            Add Step Here
          </Button>
        </div>
      </div>

      {/* Process Summary */}
      <div className="process-steps__summary">
        <div className="process-steps__summary-item">
          <span className="process-steps__summary-label">
            Total Configurations:
          </span>
          <span className="process-steps__summary-value">{steps.length}</span>
        </div>
        <div className="process-steps__summary-item">
          <span className="process-steps__summary-label">
            Configuration Type:
          </span>
          <span className="process-steps__summary-value">
            RPA Process Configuration
          </span>
        </div>
      </div>

      {/* Swap Confirmation Dialog */}
      <SwapConfirmationDialog
        isOpen={swapDialogOpen}
        onClose={handleSwapCancel}
        onConfirm={handleSwapConfirm}
        step1Title={
          swapIndices &&
          swapIndices.step1 < steps.length &&
          steps[swapIndices.step1]
            ? getConfigurationTitle(steps[swapIndices.step1], swapIndices.step1)
            : `Step ${(swapIndices?.step1 || 0) + 1}`
        }
        step2Title={
          swapIndices &&
          swapIndices.step2 < steps.length &&
          steps[swapIndices.step2]
            ? getConfigurationTitle(steps[swapIndices.step2], swapIndices.step2)
            : `Step ${(swapIndices?.step2 || 0) + 1}`
        }
        loading={isSwapping}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteStepConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        stepTitle={
          deleteStepIndex !== null &&
          deleteStepIndex < steps.length &&
          steps[deleteStepIndex]
            ? getConfigurationTitle(steps[deleteStepIndex], deleteStepIndex)
            : `Step ${(deleteStepIndex || 0) + 1}`
        }
        stepIndex={deleteStepIndex || 0}
        loading={isDeleting}
      />

      {/* Add Step Dialog */}
      <AddStepDialog
        isOpen={addDialogOpen}
        onClose={handleAddCancel}
        onConfirm={handleAddConfirm}
        insertIndex={addStepIndex}
        loading={isAdding}
      />

      {/* Toast notifications */}
      <Toast
        type="success"
        header="Steps Swapped Successfully!"
        id="swap-success"
      />
      <Toast type="error" header="Failed to Swap Steps" id="swap-error" />
      <Toast
        type="success"
        header="Step Updated Successfully!"
        id="edit-success"
      />
      <Toast type="error" header="Failed to Update Step" id="edit-error" />
      <Toast
        type="success"
        header="Step Deleted Successfully!"
        id="delete-success"
      />
      <Toast type="error" header="Failed to Delete Step" id="delete-error" />
      <Toast
        type="success"
        header="Step Added Successfully!"
        id="add-success"
      />
      <Toast type="error" header="Failed to Add Step" id="add-error" />
    </div>
  );
};

export default ProcessSteps;
